import { deriveReelSections } from '@src/services/reel/deriveReelSections';

import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { TranscriptTrack } from '../../models/TranscriptTrack';
import { EditorChangeType } from '../../types';
import { AbstractCommand } from '../AbstractCommand';
import { applyReelSectionOrder } from './applyReelSectionOrder';

import type { Scene } from '../../models/layout/Scene';
import type { VideoEditor } from '../../VideoEditor';

/**
 * PLAT-11153 — puts the reel's Sections in an arbitrary new order.
 *
 * The AI's permutation applied as **one** timeline command: the same
 * re-sequence the hand drag performs (DE6), over the whole reel at once instead
 * of one Section at a time. `applyReelSectionOrder` — built for PLAT-11056 and
 * already shared by the Section move, the Chapter move and the insert — takes
 * an arbitrary permutation and does the whole job, so this command is thin by
 * design. Re-implementing the layout maths here would be its fourth copy.
 *
 * One entry, not one per Section, for the reason {@link MoveReelChapterCommand}
 * gives: N entries would leave a fifteen-Cmd-Z undo, and each one would pass
 * through a state in which the reel is half rearranged.
 *
 * Every Section the reel has must appear exactly once in `orderedSectionIds`.
 * The caller guarantees that — a Section the model forgot has already been
 * appended and a repeat already dropped (PLAT-11099 DS8) — and this refuses
 * rather than re-laying the reel out around a Section it cannot place.
 *
 * @param orderedSectionIds Every live Section, in the order it should play
 */
export class ReorderReelSectionsCommand extends AbstractCommand {
  private orderedSectionIds: readonly string[];
  /** Reel-level elements (template intros / end cards) exempt from carry (DE7). */
  private exemptElementIds: ReadonlySet<string> | undefined;

  private executed = false;
  private originalTranscriptTrackId: string | undefined;
  /**
   * The scenes as they stood before the re-layout re-anchored them.
   * {@link TimelineSnapshot} carries tracks and elements but not scenes, so the
   * same reason {@link RemoveReelSectionCommand} keeps its own copy applies
   * here: without it an undo puts the old element times back underneath the
   * new anchors.
   */
  private previousScenes: Scene[] | undefined;

  constructor(
    editor: VideoEditor,
    orderedSectionIds: readonly string[],
    exemptElementIds?: ReadonlySet<string>,
  ) {
    super(editor);
    this.orderedSectionIds = orderedSectionIds;
    this.exemptElementIds = exemptElementIds;
  }

  getName(): string {
    return 'ReorderReelSectionsCommand';
  }

  getDescription(): string {
    return `Reorder reel into ${this.orderedSectionIds.length} section(s)`;
  }

  execute(): boolean {
    if (this.executed) return false;

    const timeline = this.editor.timeline;
    const sections = deriveReelSections(timeline);

    // A permutation of the reel, or nothing. A partial order would silently
    // drop whichever Sections it left out — a passage vanishing from someone's
    // video while the reel still looks plausible.
    if (this.orderedSectionIds.length !== sections.length) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'not_a_permutation',
        given: this.orderedSectionIds.length,
        sections: sections.length,
      });
      return false;
    }

    const byId = new Map(sections.map(section => [section.id, section]));
    const order = this.orderedSectionIds.map(id => byId.get(id));
    if (order.some(section => section === undefined)) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'section_not_found',
        sectionIds: this.orderedSectionIds,
      });
      return false;
    }
    if (
      new Set(this.orderedSectionIds).size !== this.orderedSectionIds.length
    ) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'duplicate_section',
        sectionIds: this.orderedSectionIds,
      });
      return false;
    }

    // An order that changes nothing is not an edit, and should not cost an undo
    // entry — the same judgement a drag that lands where it started makes.
    if (sections.every((section, index) => section.id === order[index]?.id)) {
      return false;
    }

    const track = timeline.tracks.find(
      (t): t is TranscriptTrack => t instanceof TranscriptTrack,
    );
    if (!track) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    super.execute(); // captures the timeline snapshot for undo

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        sectionCount: sections.length,
      });

      this.originalTranscriptTrackId = this.editor.getTranscriptTrack()?.id;
      this.previousScenes = [...timeline.scenes];

      const { restored } = applyReelSectionOrder(
        this.editor,
        track,
        sections,
        order as NonNullable<(typeof order)[number]>[],
        this.exemptElementIds,
      );
      if (!restored) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'restore_transcript_failed',
        });
      }

      // settles the captions and the pinned cards
      // against their new positions.
      this.recalculateTimeline();

      this.editor.markDirty('reorder_reel_sections');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'reel_sections_reordered',
        sectionIds: this.orderedSectionIds,
      });

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        sectionCount: sections.length,
      });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  undo(): boolean {
    if (!this.executed) return false;

    const result = super.undo();

    // The transcript restore re-pointed transcriptTrackId at the rebuilt track;
    // the snapshot restores the tracks but not that id.
    if (result && this.originalTranscriptTrackId) {
      this.editor.setTranscriptTrack(this.originalTranscriptTrackId);
    }
    if (result && this.previousScenes) {
      this.editor.timeline.setScenes(this.previousScenes);
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
